/**
 * Pure mapping for Migrado-related unit payload fields.
 * Kept free of path aliases so it can be unit-tested with Node's test runner.
 */
export type EnergyContractingModelCode = 'WHOLESALE' | 'RETAIL';

export type MigradoFormSlice = {
  isMigrated: boolean;
  migrationDate?: string | null;
  energyContractingModel?: EnergyContractingModelCode | null;
};

export type MigradoPayloadSlice = {
  migrationDate: string | null;
  energyContractingModel: EnergyContractingModelCode | null;
};

/** Empty, mask placeholder, or all-zero dates (00/00/0000, 00000000) are not real dates. */
export function isBlankDateInput(value?: string | null): boolean {
  if (value == null) return true;
  const trimmed = String(value).trim();
  if (!trimmed) return true;
  const digits = trimmed.replace(/\D/g, '');
  return !digits || /^0+$/.test(digits);
}

/**
 * Converts DDMMYYYY or DD/MM/YYYY to YYYY-MM-DD.
 * Blank/placeholder/invalid calendar dates return undefined (caller should send null).
 */
export function toIsoDateFromDdMmYyyy(value?: string | null): string | undefined {
  if (isBlankDateInput(value)) return undefined;
  const digits = String(value).replace(/\D/g, '');
  if (digits.length !== 8) return undefined;

  const dd = digits.slice(0, 2);
  const mm = digits.slice(2, 4);
  const yyyy = digits.slice(4, 8);
  const day = Number(dd);
  const month = Number(mm);
  const year = Number(yyyy);
  const date = new Date(year, month - 1, day);
  if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) {
    return undefined;
  }

  return `${yyyy}-${mm}-${dd}`;
}

export function isMigratedFromMigrationDate(migrationDate?: string | null): boolean {
  return !isBlankDateInput(migrationDate);
}

export function mapMigradoFieldsToPayload(
  data: MigradoFormSlice,
  toIsoDate: (value?: string | null) => string | undefined,
): MigradoPayloadSlice {
  if (!data.isMigrated) {
    return {
      migrationDate: null,
      energyContractingModel: null,
    };
  }

  return {
    migrationDate: toIsoDate(data.migrationDate) ?? null,
    energyContractingModel: data.energyContractingModel ?? null,
  };
}
